import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '') + '/api';
const BOT_API_BASE_URL = process.env.REACT_APP_BOT_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

const botApi = axios.create({
  baseURL: BOT_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

export const getTokens = async (filters = {}) => {
  const params = new URLSearchParams();
  
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value);
    }
  });
  
  const response = await api.get(`/tokens?${params.toString()}`);
  
  // Récupérer les stats des tokens filtrés depuis les en-têtes
  const headers = response.headers;
  const totalCount = parseInt(headers['x-total-count'] || '0', 10);
  const autoBuyCount = parseInt(headers['x-auto-buy-count'] || '0', 10);
  const honeypotCount = parseInt(headers['x-honeypot-count'] || '0', 10);
  const socialCount = parseInt(headers['x-social-count'] || '0', 10);
  const avgScore = parseFloat(headers['x-average-score'] || '0');
  const chainCount = parseInt(headers['x-chain-count'] || '0', 10);
  
  return {
    tokens: response.data,  // ✅ FIX: Renommer 'data' en 'tokens' pour correspondre au frontend
    total: totalCount,      // ✅ FIX: Renommer 'totalCount' en 'total' pour correspondre au frontend
    stats: {
      total_tokens: totalCount,
      auto_buy_count: autoBuyCount,
      honeypot_count: honeypotCount,
      social_count: socialCount,
      average_score: avgScore,
      chain_count: chainCount,
    }
  };
};

export const getToken = async (tokenId) => {
  const response = await api.get(`/tokens/${tokenId}`);
  return response.data;
};

export const getStats = async () => {
  const response = await api.get('/stats');
  return response.data;
};

export const getChains = async () => {
  const response = await api.get('/chains');
  return response.data;
};

export const getSources = async (days) => {
  // Si days est un objet (React Query Context), ignorer ou extraire la vraie valeur
  let actualDays = days;
  if (typeof days === 'object' && days !== null) {
    // C'est probablement un QueryFunctionContext, ignorer
    actualDays = undefined;
  }
  
  const url = actualDays ? `/sources?days=${encodeURIComponent(actualDays)}` : '/sources';
  const response = await api.get(url);
  return response.data;
};

export const deleteToken = async (tokenId) => {
  const response = await api.delete(`/tokens/${tokenId}`);
  return response.data;
};

export const closePosition = async (positionId) => {
  const response = await botApi.post(`/portfolio/positions/${positionId}/close`);
  return response.data;
};

export const closePositionsBatch = async (ids, emergency = true) => {
  const response = await botApi.post('/portfolio/positions/close-batch', { ids, emergency });
  return response.data;
};

// ---- Cockpit V1 ----
export const getRiskState = async () => (await api.get('/risk/state')).data;
export const getOnchainErrors = async (window = 300) =>
  (await api.get(`/onchain/errors?window=${window}`)).data;
export const getSystemInstant = async () => (await api.get('/system/instant')).data;
export const getSystemHealth = async () => (await api.get('/system/health')).data;
export const getSystemResources = async () => (await api.get('/system/resources')).data;
export const getWalletBalance = async () => (await api.get('/wallet/balance')).data;
export const getActivePositions = async (limit = 5) =>
  (await api.get(`/positions/active?skip=0&limit=${limit}`)).data;
// Stats POSITIONS (closed_positions.realized_pnl/win_rate + active_positions) — PAS /api/stats (= stats tokens).
export const getPositionsStats = async () => (await api.get('/positions/stats')).data;
export const triggerPanic = async (action) =>
  (await api.post('/control/panic', { confirm: true, action })).data;
// Bandeau avertissement livre papier optimiste (ADR 0016, biais slippage) — public, no auth.
export const getPaperBookCaveat = async () => (await api.get('/paper-book-caveat')).data;

// ---- Performance charts (V1) — period/mode/shadow communs (cf contrat) ----
const perfParams = ({ period, mode, shadow } = {}) => {
  const p = new URLSearchParams();
  if (period) p.append('period', period);
  if (mode) p.append('mode', mode);
  if (shadow) p.append('shadow', shadow);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
};
export const getEquityCurve = async (opts = {}) =>
  (await api.get(`/performance/equity-curve${perfParams(opts)}`)).data;
export const getDailyPnl = async (opts = {}) =>
  (await api.get(`/performance/daily-pnl${perfParams(opts)}`)).data;
export const getDistribution = async (opts = {}) =>
  (await api.get(`/performance/distribution${perfParams(opts)}`)).data;
export const getExitReasons = async (opts = {}) =>
  (await api.get(`/performance/exit-reasons${perfParams(opts)}`)).data;
export const getPerfByHour = async (opts = {}) =>
  (await api.get(`/performance/by-hour${perfParams(opts)}`)).data;
export const getPerfByDow = async (opts = {}) =>
  (await api.get(`/performance/by-dow${perfParams(opts)}`)).data;

// ---- Créateurs suivis ----
export const getTrackedCreators = async ({ sort = 'pump_rate_30pct_30d', sortDir = 'desc', minPeakMedian = null, minWr = null, maxRug = null, trackedOnly = false, minTokens = null, migrated = null, almostBonded = false, armState = null, limit = 100, skip = 0 } = {}) => {
  const p = new URLSearchParams({ sort, sort_dir: sortDir, limit, skip });
  if (minPeakMedian != null) p.set('min_peak_median', minPeakMedian);
  if (minWr != null) p.set('min_wr', minWr);
  if (maxRug != null) p.set('max_rug', maxRug);
  if (trackedOnly) p.set('tracked_only', 'true');
  if (minTokens != null) p.set('min_tokens', minTokens);
  if (migrated != null) p.set('migrated', migrated);
  if (almostBonded) p.set('almost_bonded', 'true');
  if (armState != null) p.set('arm_state', armState);
  return (await api.get(`/creators/tracked?${p.toString()}`)).data;
};

// ---- Wallets copiés ----
export const getCopiedWallets = async ({ sort = 'sol_per_day', sortDir = 'desc', minPeakMedian = null, minWr = null, maxRug = null, activeOnly = false, migrated = null, minTokens = null, almostBonded = false, limit = 100, skip = 0 } = {}) => {
  const p = new URLSearchParams({ sort, sort_dir: sortDir, limit, skip });
  if (minPeakMedian != null) p.set('min_peak_median', minPeakMedian);
  if (minWr != null) p.set('min_wr', minWr);
  if (maxRug != null) p.set('max_rug', maxRug);
  if (activeOnly) p.set('active_only', 'true');
  if (migrated != null) p.set('migrated', migrated);
  if (minTokens != null) p.set('min_tokens', minTokens);
  if (almostBonded) p.set('almost_bonded', 'true');
  return (await api.get(`/wallets/copied?${p.toString()}`)).data;
};

const buildTokenParams = ({ sort = 'peak', sortDir = 'desc', minPeak, migratedOnly = false, rugged, tpMin, tpMax, tradedOnly = false, source, lifeMin, lifeMax, createdAfter, limit = 100, skip = 0 } = {}) => {
  const p = new URLSearchParams({ sort, sort_dir: sortDir, migrated_only: migratedOnly, limit, skip });
  if (minPeak != null) p.set('min_peak', minPeak);
  if (rugged != null) p.set('rugged', rugged);          // 'true' | 'false'
  if (tpMin != null) p.set('tp_min', tpMin);
  if (tpMax != null) p.set('tp_max', tpMax);
  if (tradedOnly) p.set('traded_only', true);
  if (source != null) p.set('source', source);
  if (lifeMin != null) p.set('life_min', lifeMin);
  if (lifeMax != null) p.set('life_max', lifeMax);
  if (createdAfter != null) p.set('created_after', createdAfter);
  return p.toString();
};

export const getCreatorTokens = async (addr, opts = {}) =>
  (await api.get(`/creators/${addr}/tokens?${buildTokenParams(opts)}`)).data;

export const getWalletTokens = async (wallet, opts = {}) =>
  (await api.get(`/wallets/${wallet}/tokens?${buildTokenParams(opts)}`)).data;

// ---- Backtests (reconstruction Helius slot-1 × TP) ----
export const postBacktestTarget = async ({ kind, address, params }) =>
  (await api.post('/backtest/target', { kind, address, params })).data;
export const getBacktestJobs = async ({ status = null, kind = null, limit = 50, skip = 0 } = {}) => {
  const p = new URLSearchParams({ limit, skip });
  if (status) p.set('status', status);
  if (kind) p.set('kind', kind);
  return (await api.get(`/backtest/jobs?${p.toString()}`)).data;
};
export const getBacktestJob = async (jobId) =>
  (await api.get(`/backtest/jobs/${jobId}`)).data;
export const getBacktestByEntity = async (kind, address) =>
  (await api.get(`/backtest/by-entity/${kind}/${encodeURIComponent(address)}`)).data;
export const deleteBacktestJob = async (jobId) =>
  (await api.delete(`/backtest/jobs/${jobId}`)).data;

// ---- Découverte / règles minées (#3a) ----
export const getDiscoveryRuns = async () =>
  (await api.get('/discovery/runs')).data;
export const getDiscoveryRules = async ({ runId = null, sort = 'lift_oos', order = 'desc', minSupport = null, limit = 200, skip = 0, minEvMedian = undefined, minWr = undefined, minSolPnl = undefined, onlyBacktested = undefined, minPeakMedian = undefined, maxRug = undefined } = {}) => {
  const p = new URLSearchParams({ sort, order, limit, skip });
  if (runId) p.set('run_id', runId);
  if (minSupport != null) p.set('min_support', minSupport);
  if (minEvMedian !== undefined && minEvMedian !== '') p.set('min_ev_median', minEvMedian);
  if (minWr !== undefined && minWr !== '') p.set('min_wr', minWr);
  if (minSolPnl !== undefined && minSolPnl !== '') p.set('min_sol_pnl', minSolPnl);
  if (onlyBacktested) p.set('only_backtested', 'true');
  if (minPeakMedian !== undefined && minPeakMedian !== '') p.set('min_peak_median', minPeakMedian);
  if (maxRug !== undefined && maxRug !== '') p.set('max_rug', maxRug);
  return (await api.get(`/discovery/rules?${p.toString()}`)).data;
};
export const getDiscoveryRule = async (id) =>
  (await api.get(`/discovery/rules/${id}`)).data;
export const postRuleBacktest = async (id, params) =>
  (await api.post(`/discovery/rules/${id}/backtest`, params)).data;
export const getRuleBacktest = async (id) =>
  (await api.get(`/discovery/rules/${id}/backtest`)).data;
export const postRunBacktestAll = async (runId) =>
  (await api.post(`/discovery/runs/${runId}/backtest-all`)).data;

// ---- Config & Shadow des règles (#3b) ----
export const getRuleActivations = async () =>
  (await api.get('/discovery/rules/activations')).data;
export const putRuleActivation = async (body) =>
  (await api.put('/discovery/rules/activation', body)).data;
export const deleteRuleActivation = async (id) =>
  (await api.delete(`/discovery/rules/activation/${id}`)).data;
export const getShadowStats = async ({ windowHours = 24 } = {}) =>
  (await api.get(`/discovery/shadow/stats?window_hours=${windowHours}`)).data;
export const getShadowEvents = async ({ activationId = null, source = null, limit = 100, skip = 0 } = {}) => {
  const p = new URLSearchParams({ limit, skip });
  if (activationId != null) p.set('activation_id', activationId);
  if (source) p.set('source', source);
  return (await api.get(`/discovery/shadow/events?${p.toString()}`)).data;
};

// ---- Copytrade discovery (A6/A7) ----
export const getCopytradeCandidates = async () =>
  (await api.get('/copytrade/candidates')).data;
export const acceptCandidate = async (wallet, body) =>
  (await api.post(`/copytrade/candidates/${encodeURIComponent(wallet)}/accept`, body)).data;
export const rejectCandidate = async (wallet) =>
  (await api.post(`/copytrade/candidates/${encodeURIComponent(wallet)}/reject`)).data;
export const getCopytradeConfig = async () =>
  (await api.get('/copytrade/config')).data;
export const putCopytradeConfig = async (body) =>
  (await api.put('/copytrade/config', body)).data;
export const runCopytradeDiscovery = async () =>
  (await api.post('/copytrade/discovery/run')).data;
export const getDiscoveryStatus = async () =>
  (await api.get('/copytrade/discovery/status')).data;
export const deactivateCopytradeWallet = async (wallet) =>
  (await api.post(`/copytrade/wallets/${encodeURIComponent(wallet)}/deactivate`)).data;

// ---- Créateurs — décisions + seuils de tracking (B4/B5) ----
export const postCreatorDecision = async (addr, body) =>
  (await api.post(`/creators/${encodeURIComponent(addr)}/decision`, body)).data;
export const getCreatorTrackingConfig = async () =>
  (await api.get('/creators/tracking-config')).data;
export const putCreatorTrackingConfig = async (body) =>
  (await api.put('/creators/tracking-config', body)).data;
export const reapplyCreatorTracking = async () =>
  (await api.post('/creators/tracking/reapply')).data;

// ---- Armement (SP1) ----
export const getArmingEntities = async () =>
  (await api.get('/arming/entities')).data;
export const putArmingEntity = async (body) =>
  (await api.put('/arming/entities', body)).data;
export const deleteArmingEntity = async (id) =>
  (await api.delete(`/arming/entities/${id}`)).data;
export const getArmingConfig = async () =>
  (await api.get('/arming/config')).data;
export const putArmingConfig = async (body) =>
  (await api.put('/arming/config', body)).data;
export const putRuleMode = async (id, mode) =>
  (await api.put(`/discovery/rules/activation/${id}/mode`, { mode })).data;
export const getArmingContract = async () =>
  (await api.get('/arming/contract')).data;
export const getArmingExclusive = async () =>
  (await api.get('/arming/exclusive')).data;
export const putArmingExclusive = async (enabled) =>
  (await api.put('/arming/exclusive', { enabled })).data;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dashboard_access_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Never intercept the refresh call itself — prevents infinite loop
    if (originalRequest.url === '/auth/refresh') {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Another refresh is in flight — queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      const refreshToken = localStorage.getItem('dashboard_refresh_token');
      if (refreshToken) {
        isRefreshing = true;
        try {
          const res = await api.post('/auth/refresh', { refresh_token: refreshToken });
          const newToken = res.data.access_token;
          localStorage.setItem('dashboard_access_token', newToken);
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.removeItem('dashboard_access_token');
          localStorage.removeItem('dashboard_refresh_token');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      localStorage.removeItem('dashboard_access_token');
      localStorage.removeItem('dashboard_refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
